import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import '../../css/forms/ApplicationForms.css';

const API_URL = 'https://rm-backend-service-505761517154.us-central1.run.app/submit-soluna-ambassador';

const SolunaAmbassador = () => {
  const [formData, setFormData] = useState({
    first_name: '', 
    last_name: '',
    email: '',
    phone: '',
    university: '',
    graduation_year: '',
    college_address: '',
    work_status: '',
    shirt_size: '',
    past_experience: '',
    interest_reason: '',
    additional_notes: '',
    resume_url: '',
    instagram_url: '',
    tiktok_url: '',
    cover_letter_url: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 1400);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (submitAttempted && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = [
      'first_name', 'last_name', 'email', 'phone', 
      'university', 'graduation_year', 'college_address',
      'work_status', 'shirt_size', 'past_experience', 'interest_reason',
      'resume_url', 'instagram_url', 'tiktok_url'
    ];

    requiredFields.forEach(field => {
      if (!formData[field] || (typeof formData[field] === 'string' && !formData[field].trim())) {
        errors[field] = 'This field is required';
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitAttempted(true);
  setIsSubmitting(true);
  window.scrollTo({ top: 1400, behavior: 'smooth' });

  if (validateForm()) {
    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get("content-type");

      if (!response.ok) {
        // Try to parse error if it's JSON, otherwise fallback to text
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to submit application');
        } else {
          const errorText = await response.text();
          throw new Error(`Server error: ${errorText.slice(0, 100)}...`);
        }
      }

      const data = await response.json(); // Safe to parse
      console.log('Success:', data);
      setSubmitted(true);
      setShowThankYouModal(true);
      window.scrollTo(0, 1400);

    } catch (error) {
      console.error('Error submitting form:', error.message);
      alert(error.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  } else {
    setIsSubmitting(false);
    const firstErrorField = document.querySelector('.error');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};


  const closeModal = () => {
    setShowThankYouModal(false);
    navigate('/roles');
    window.scrollTo(0, 0);
  };

  if (submitted) {
    return (
      <div className="aam-container thank-you-page">
        <Helmet>
          <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
          rel="stylesheet" />
        </Helmet>
        
        {showThankYouModal && (
          <div className="thank-you-modal">
            <div className="thank-you-content">
              <h1>Thank You for Applying!</h1>
              <p>We've received your application for the Soluna Campus Ambassador position.</p>
              <p>If we feel you're a good fit for the role, we'll email you with next steps soon.</p>
              <button onClick={closeModal}>Go to Roles</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="aam-container">
      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" 
        rel="stylesheet" />
      </Helmet>
      
      <div className="aam-header">
        <h1>Soluna Campus Ambassador</h1>
        <div className="aam-meta">
          <span className="meta-item">Location: NJ Campuses</span>
          <span className="meta-item">Job Type: Part-Time</span>
        </div>
      </div>

      <div className="aam-content">
        <section className="aam-section aam-about">
          <h2>About Soluna</h2>
          <p>Soluna is a free, state-funded mental wellness platform for New Jersey teens and students that offers evidence-based tools, moderated peer spaces, and real human coaching/counseling (not AI). It's available in English and Spanish via app or web, with strong safety and privacy practices. Learn more at <a href="https://solunaapp.com/newjersey">solunaapp.com/newjersey</a>.</p>
        </section>

        <section className="aam-section aam-description">
          <h2>Why Join Us?</h2>
          <ul className="aam-list">
            <li><strong>Exclusive Opportunity:</strong> Limited roles per campus in NJ.</li>
            <li><strong>Make an Impact:</strong> Help normalize mental wellness and connect students to free support.</li>
            <li><strong>Professional Growth:</strong> Build real outreach, events, and communications experience while collaborating with an established team.</li>
          </ul>
        </section>

        <section className="aam-section aam-responsibilities">
          <h2>Position Details</h2>
          <p>As a Soluna Campus Ambassador, you'll be the face of the brand on your campus, educating peers, building relationships with org leaders and staff, and driving grassroots awareness.</p>
          <ul className="aam-list">
            <li><strong>Time Commitment:</strong> ~5 hours/week</li>
            <li><strong>Program Length:</strong> 6 weeks (target window: Oct 20 to Nov 30)</li>
            <li><strong>Locations:</strong> NJ campuses (e.g., Rutgers, Kean, Rowan)</li>
          </ul>
          
          <h3>Your responsibilities will include:</h3>
          <ul className="aam-list">
            <li><strong>Effective Communication:</strong> Explain Soluna clearly (free, state-funded, confidential; no ads, no paywalls, no insurance required). Emphasize coaches are real humans and not AI.</li>
            <li><strong>Tabling & Events:</strong> Host engaging on-campus activations; give 5 to 10 minute intros in classes and club meetings using provided talk tracks and materials.</li>
            <li><strong>Partnership Outreach:</strong> Proactively contact RAs, counseling centers, professors, and student org leaders to schedule drop-ins or materials drops (use the intro email templates).</li>
            <li><strong>Collateral Distribution:</strong> Hand out quick cards (QR to download), stickers, and totes; hang flyers in approved locations.</li>
            <li><strong>Brand & Safety Standards:</strong> Keep interactions non-transactional (no "download for swag" exchanges); follow platform safety norms (no user-to-user DMs; pre-moderated forums).</li>
            <li><strong>Insights & Reporting:</strong> Share weekly learnings: common questions, what messaging resonated, hot spots, and event/partnership recommendations.</li>
            <li><strong>Eligibility:</strong> Current college students in New Jersey. (You'll primarily reach 18+ undergrads; Soluna's full feature set is for ages 13 to 18, with most self-guided features available 18+.)</li>
          </ul>
        </section>

        <section className="aam-section aam-benefits">
          <h2>What's in it for you?</h2>
          <ul className="aam-list">
            <li><strong>Paid Experience:</strong> Competitive stipend for a short, high-impact program. (Soluna's ambassador initiatives are paid opportunities.)</li>
            <li><strong>Exclusive Merch & Materials:</strong> Branded gear, stickers, totes, flyers, and more to fuel outreach.</li>
            <li><strong>Career Growth:</strong> Hands-on field marketing, partnerships, and campaign reporting experience.</li>
            <li><strong>Networking:</strong> Collaborate with Soluna's marketing team and Romulus program managers; build campus relationships.</li>
          </ul>
        </section>

        <section className="aam-section aam-application">
          <h2>How to Apply</h2>
          <p>If you're passionate about mental wellness and ready to make a difference on your campus, apply now! We'll reach out if you're a good fit.
            If you have any questions, please email <b>walt@romulusmedia.com</b>.
          </p>
          <p className="aam-emphasis">This opportunity is presented by Romulus Media.</p>
        </section>

        <form onSubmit={handleSubmit} className="aam-form">
          <h2 className="form-title">Application Form</h2>
          
          {submitAttempted && Object.keys(formErrors).length > 0 && (
            <div className="form-error-message">
              Please correct the highlighted errors:
            </div>
          )}

          <div className="form-grid">
            <div className="form-group">
              <label className="question-text">First Name *</label>
              <input 
                type="text" 
                name="first_name" 
                value={formData.first_name} 
                onChange={handleChange} 
                className={formErrors.first_name ? 'error' : ''}
              />
              {formErrors.first_name && <span className="error-message">{formErrors.first_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Last Name *</label>
              <input 
                type="text" 
                name="last_name" 
                value={formData.last_name} 
                onChange={handleChange} 
                className={formErrors.last_name ? 'error' : ''}
              />
              {formErrors.last_name && <span className="error-message">{formErrors.last_name}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University Email *</label>
              <input 
                type="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange} 
                className={formErrors.email ? 'error' : ''}
              />
              {formErrors.email && <span className="error-message">{formErrors.email}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Phone *</label>
              <input 
                type="tel" 
                name="phone" 
                value={formData.phone} 
                onChange={handleChange} 
                className={formErrors.phone ? 'error' : ''}
              />
              {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">University/College Name *</label>
              <input 
                type="text" 
                name="university" 
                value={formData.university} 
                onChange={handleChange} 
                className={formErrors.university ? 'error' : ''}
              />
              {formErrors.university && <span className="error-message">{formErrors.university}</span>}
            </div>

            <div className="form-group">
              <label className="question-text">Expected Year of Graduation *</label>
              <input 
                type="text" 
                name="graduation_year" 
                value={formData.graduation_year} 
                onChange={handleChange} 
                className={formErrors.graduation_year ? 'error' : ''}
              />
              {formErrors.graduation_year && <span className="error-message">{formErrors.graduation_year}</span>}
            </div>
            
            <div className="form-group full-width">
              <label className="question-text">What is your college address - dorm, apartment, housing, etc.? *</label>
              <input 
                type="text" 
                name="college_address" 
                value={formData.college_address} 
                onChange={handleChange} 
                className={formErrors.college_address ? 'error' : ''}
              />
              {formErrors.college_address && <span className="error-message">{formErrors.college_address}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Are you a U.S. citizen or authorized to work in the United States? (please note that if the answer is "No", you will still be considered.) *</label>
              <div className="radio-group">
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="yes"
                    checked={formData.work_status === "yes"}
                    onChange={handleChange}
                  />
                  Yes
                </label>
                <label className="label-select">
                  <input
                    type="radio"
                    name="work_status"
                    value="no"
                    checked={formData.work_status === "no"}
                    onChange={handleChange}
                  />
                  No
                </label>
              </div>
              {formErrors.work_status && <span className="error-message">{formErrors.work_status}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What is your shirt size? *</label>
              <select 
                name="shirt_size" 
                value={formData.shirt_size} 
                onChange={handleChange} 
                className={formErrors.shirt_size ? 'error' : ''}
              >
                <option value="">Select your shirt size</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="XXL">XXL</option>
                <option value="XXXL">XXXL</option>
              </select>
              {formErrors.shirt_size && <span className="error-message">{formErrors.shirt_size}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Have you previously served as a campus ambassador? 
                If yes, which company or organization was it for, and what were your main responsibilities? *</label>
              <textarea 
                name="past_experience" 
                value={formData.past_experience} 
                onChange={handleChange} 
                className={formErrors.past_experience ? 'error' : ''}
                rows="3"
              />
              {formErrors.past_experience && <span className="error-message">{formErrors.past_experience}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">What clubs, organizations, or communities on campus are you involved in, 
                and how would you leverage those for this role? *</label>
              <textarea 
                name="interest_reason" 
                value={formData.interest_reason} 
                onChange={handleChange} 
                className={formErrors.interest_reason ? 'error' : ''}
                rows="3"
              />
              {formErrors.interest_reason && <span className="error-message">{formErrors.interest_reason}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Resume URL * (Google Drive, Dropbox, etc.)</label>
              <input 
                type="text" 
                name="resume_url" 
                value={formData.resume_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-resume"
                className={formErrors.resume_url ? 'error' : ''}
              />
              {formErrors.resume_url && <span className="error-message">{formErrors.resume_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Cover Letter + references URL (optional). For multiple
                links, separate with commas.
              </label>
              <input 
                type="text" 
                name="cover_letter_url" 
                value={formData.cover_letter_url} 
                onChange={handleChange} 
                placeholder="drive.google.com/your-cover-letter"
              />
            </div>

            <div className="form-group full-width">
              <label className="question-text">Instagram URL *</label>
              <input 
                type="text" 
                name="instagram_url" 
                value={formData.instagram_url} 
                onChange={handleChange} 
                placeholder="instagram.com/your-username"
                className={formErrors.instagram_url ? 'error' : ''}
              />
              {formErrors.instagram_url && <span className="error-message">{formErrors.instagram_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">TikTok URL *</label>
              <input 
                type="text" 
                name="tiktok_url" 
                value={formData.tiktok_url} 
                onChange={handleChange} 
                placeholder="tiktok.com/@your-username"
                className={formErrors.tiktok_url ? 'error' : ''}
              />
              {formErrors.tiktok_url && <span className="error-message">{formErrors.tiktok_url}</span>}
            </div>

            <div className="form-group full-width">
              <label className="question-text">Additional notes (optional)</label>
              <textarea 
                name="additional_notes" 
                value={formData.additional_notes} 
                onChange={handleChange} 
                rows="3"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SolunaAmbassador;
